import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import Table from "@/common/elements/Table";
import AliasAddForm from "@/components/BookingsDetails/PersonalInformationDetails/AliasAddForm";
import {
  InmateDetailsTableContainer,
  TableHeader,
} from "../../InmateDetails/styles";
import Button from "@/common/elements/Button";
import { Popconfirm } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import CustomModal from "../../CustomModal";
import API_ENDPOINTS from "@/util/api";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import { hasCheckSecurity, isNonEmptyVariable } from "@/util/common_function";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";
import { saveExtraTableRecord } from "@/util/extraColumnActions";

const ALIASES_HEADERS = [
  { key: "lName", text: "LName" },
  { key: "fName", text: "FName" },
  { key: "mName", text: "MName" },
  { key: "dob", text: "Date of Birth" },
  { key: "ssn", text: "SSN" },
  { key: "suffix", text: "Suffix" },
  { key: "questionedIdentity", text: "Questioned Identity" },
];

export const AliasesList = forwardRef(
  ({
    showQuickAddForm = true,
    selectedAliases,
    associates,
    onAliasesChange,
    startingPoint="",
    // Optional (permission) => boolean override for callers outside
    // Bookings (e.g. Civil Papers) that must check their own module's
    // rights instead of Bookings' Arrest Report sub-module.
    securityCheck = null,
  }, ref) => {
    const checkSecurity = (permission) =>
      securityCheck
        ? securityCheck(permission)
        : hasCheckSecurity(
            Security_Types.functionality,
            Entity_types.subModule,
            permission,
            MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
            SUB_MODULES_LIST_WITH_ID.Arrest_Report
          );
    const aliasFormRef = useRef();
    const dispatch = useDispatch();
    const { bookings, bookingsFieldsEnabled } = useSelector(
      (state) => state.bookings
    );

    // "Config" — drag-reorder/resize columns, persisted per-user (see
    // docs/plans/grid-column-config-feature.md). Shared across modules
    // (Bookings, Warrants, ...) — gridId must carry the caller's module
    // context via startingPoint, same fix as AddressesList.jsx, or two
    // different screens end up sharing one saved column layout.
    const {
      headers: aliasHeaders,
      isConfiguring,
      configButtonLabel,
      startConfiguring,
      confirmSave,
      discardChanges,
      dataTableRef,
    } = useGridColumnConfig(
      `${startingPoint || "Bookings"}:BioAliases`,
      ALIASES_HEADERS
    );

    const initialAliases = associates
      ? (selectedAliases || []).map((a) => ({
          ...a,
          id: a.id || uuidv4(),
        }))
      : selectedAliases || bookings?.bio?.[0]?.bioAliases || [];

    const [aliasData, setAliasData] = useState(initialAliases);
    const [showModal, setShowModal] = useState(false);
    const [empty, setEmpty] = useState(false);

    const [selectAlias, setSelectAlias] = useState({
      lName: "",
      fName: "",
      mName: "",
      suffix: "",
      dob: "",
      ssn: "",
      questionedIdentity: "",
    });
    const [showEdit, setShowEdit] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);

    // When bookings or selectedAliases change, update aliasData accordingly.
    useEffect(() => {
      if (associates) {
        setAliasData(
          (selectedAliases || []).map((a) => ({
            ...a,
            id: a.id || uuidv4(),
          }))
        );
      } else {
        let aliases = bookings?.bio?.[0]?.bioAliases || [];
        aliases = aliases.map((al) => ({
          ...al,
          lName: al.lName,
          fName: al.fName,
          mName: al.mName,
          suffix: al.suffix,
          dob: al.dob,
          ssn: al.ssn,
          questionedIdentity: al.questionedIdentity,
          id: al.id || uuidv4(),
        }));
        setAliasData(aliases);
      }
    }, [selectedAliases, bookings, associates]);

    // Use a ref to store previous aliasData.
    const prevAliasDataRef = useRef();
    useEffect(() => {
      const prev = prevAliasDataRef.current;
      const stripId = (alias) => {
        const { id, ...rest } = alias;
        return rest;
      };

      const prevDataWithoutId = JSON.stringify(
        prevAliasDataRef.current?.map(stripId)
      );
      const currentDataWithoutId = JSON.stringify(aliasData.map(stripId));

      if (prevDataWithoutId === currentDataWithoutId) {
        return;
      }
      prevAliasDataRef.current = aliasData;
      // Only update if associates mode is true.
      if (associates) {
        if (typeof onAliasesChange === "function") {
          onAliasesChange(aliasData);
        }
      } else {
        const bioAliases = bookings?.bio?.[0]?.bioAliases;
        if (JSON.stringify(bioAliases) !== JSON.stringify(aliasData)) {
          dispatch(
            setBookings({
              bio: [
                {
                  ...bookings?.bio?.[0],
                  bioAliases: aliasData,
                },
              ],
            })
          );
        }
      }
    }, [aliasData]);

    // Toggle modal visibility.
    const handleAssociateseModal = () => {
      setShowModal((prev) => !prev);
    };

    // When user clicks Add, clear selection and open modal.
    const handleAddAssociates = () => {
      setSelectAlias({
        lName: "",
        fName: "",
        mName: "",
        suffix: "",
        dob: "",
        ssn: "",
        questionedIdentity: "",
      });
      setShowEdit(false);
      handleAssociateseModal();
    };

    // When a table row is clicked, set the selected alias and enable edit/delete.
    const selectAliasDataFn = (value) => {
      setSelectAlias({ ...value });
      setShowEdit(true);
      setEmpty(() => true);
    };

    // When user clicks Details.
    const editAlias = () => {
      setShowEdit(true);
      handleAssociateseModal();
    };

    // When user clicks Delete, remove the selected alias.
    const deleteAlias = () => {
      setAliasData((prev) => prev.filter((p) => p?.id !== selectAlias?.id));
      const url = API_ENDPOINTS.bioAliases + "/id/" + selectAlias?.bioAliasId;
      dispatch(setDeleteAPis(url));
      closeModal();
    };

    const closeModal = () => {
      setSelectAlias({
        lName: "",
        fName: "",
        mName: "",
        dob: "",
        ssn: "",
        suffix: "",
        questionedIdentity: false,
      });
      setShowModal(false);
      setShowEdit(false);
    };

    // Save alias from the form.
    const saveClick = () => {
      aliasFormRef?.current
        ?.validateFields()
        .then((values) => {
          values.dob = values?.dob
            ? dayjs(values.dob).format("MM/DD/YYYY")
            : "";

          // Split BioAliasesExtra's dynamic fields out of `values` — they're
          // saved separately via the generic extra-table endpoint (only
          // possible for an existing alias, which already has a real
          // bioAliasId; a brand new one only gets one after the main
          // booking save, which this modal doesn't wait for), not folded
          // into the aliasData this component dispatches into
          // bio[0].bioAliases.
          const extraFieldKeys = aliasFormRef?.current?.bioAliasesExtraFieldKeys || [];
          const extraTypeMap = aliasFormRef?.current?.bioAliasesExtraTypeMap || {};
          const extraRecord = {};
          extraFieldKeys.forEach((key) => {
            if (key in values) {
              const val = values[key];
              const htmlType = extraTypeMap[key];
              extraRecord[key] = dayjs.isDayjs(val)
                ? val.format(htmlType === "datetime" ? "MM/DD/YYYY HH:mm" : "MM/DD/YYYY")
                : val;
              delete values[key];
            }
          });

          if (extraFieldKeys.length > 0 && isNonEmptyVariable(selectAlias?.bioAliasId)) {
            saveExtraTableRecord({
              tableName: "BioAliasesExtra",
              idColumn: "bioAliasID",
              idValue: selectAlias.bioAliasId,
              extraRecord: {
                ...extraRecord,
                bioAliasesExtraID: aliasFormRef?.current?.bioAliasesExtraID || undefined,
              },
              extraIdKey: "bioAliasesExtraID",
            }).catch((error) => {
              console.error("[AliasesList] BioAliasesExtra save failed:", error);
            });
          }

          if (showEdit) {
            setAliasData((prev) =>
              prev.map((p) =>
                p?.id === selectAlias?.id
                  ? { ...values, bioAliasId: selectAlias.bioAliasId, id: p.id }
                  : p
              )
            );
          } else {
            setAliasData((prev) => [...prev, { ...values, id: uuidv4() }]);
          }
          closeModal();
          aliasFormRef?.current?.resetForm();
        })
        .catch((err) => {
          console.error(err);
        });
    };

    // Enable buttons only if bookingsFieldsEnabled is true.
    const isEnabled = bookingsFieldsEnabled;
    useEffect(() => {
      if (showEdit) {
        let showSave = checkSecurity(LABELS_CAPITAL.MODIFY);
        if (showSave) {
          setIsDisabled(false);
        } else {
          setIsDisabled(true);
        }
      } else {
        setIsDisabled(false);
      }
    }, [showEdit]);
    useEffect(() => {
      console.log({ isDisabled });
    }, [isDisabled]);
    return (
      <>
        {showQuickAddForm && (
          <AliasAddForm
            formData={empty ? null : selectAlias}
            isQuickAdd={true}
            ref={aliasFormRef}
            quickSave={saveClick}
            bookingsFieldsEnabled={isEnabled}
            showEdit={showEdit}
            startingPoint={startingPoint}
          />
        )}
        {/* List */}
        <InmateDetailsTableContainer>
          <TableHeader>
            <h6>List of aliases</h6>
            <div className="right">
              {
                <Button
                  onClick={handleAddAssociates}
                  disabled={
                    !isEnabled ||
                    isConfiguring ||
                    !checkSecurity(LABELS_CAPITAL.ADD)
                  }
                >
                  Add Alias
                </Button>
              }
              {
                <Button
                  onClick={editAlias}
                  style={{ marginLeft: "10px" }}
                  disabled={
                    !showEdit ||
                    isConfiguring ||
                    !checkSecurity(LABELS_CAPITAL.VIEW)
                  }
                >
                  Details
                </Button>
              }
              {
                <Popconfirm
                  title="Delete the Alias"
                  description="Are you sure to delete this Alias?"
                  onConfirm={deleteAlias}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    style={{ marginLeft: "10px" }}
                    disabled={
                      !showEdit ||
                      !isEnabled ||
                      isConfiguring ||
                      !checkSecurity(LABELS_CAPITAL.DELETE)
                    }
                  >
                    Delete
                  </Button>
                </Popconfirm>
              }

              {/* Config — drag-reorder/resize columns; only while Modify is active */}
              {!isConfiguring ? (
                <Button
                  htmlType="button"
                  onClick={startConfiguring}
                  style={{ marginLeft: "10px" }}
                >
                  {configButtonLabel}
                </Button>
              ) : (
                <Popconfirm
                  title="Do you want to save the current list view configuration?"
                  onConfirm={confirmSave}
                  onCancel={discardChanges}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ disabled: false }}
                  cancelButtonProps={{ disabled: false }}
                >
                  <Button
                    htmlType="button"
                    style={{ marginLeft: "10px" }}
                  >
                    {configButtonLabel}
                  </Button>
                </Popconfirm>
              )}
            </div>
          </TableHeader>

          {checkSecurity(LABELS_CAPITAL.VIEW) && (
            <Table
              ref={dataTableRef}
              headers={aliasHeaders}
              draggable={isConfiguring}
              data={aliasData?.map((a) => ({
                ...a,
                questionedIdentity: a?.questionedIdentity ? "True" : "False",
              }))}
              onRowClick={selectAliasDataFn}
              onRowDoubleClick={(value) => {
                selectAliasDataFn(value);
                editAlias();
              }}
            />
          )}
        </InmateDetailsTableContainer>

        <CustomModal
          header="Alias Information"
          show={showModal}
          designPattern="ant"
          onHide={closeModal}
          onSaveClick={saveClick}
          onCloseClick={closeModal}
          submitButtonDisabled={!isEnabled}
          width={"55%"}
          bodyStyle={{ padding: "20px" }}
          isDisabled={!isDisabled && !isEnabled}
        >
          {showModal && (
            <AliasAddForm
              formData={selectAlias}
              ref={aliasFormRef}
              bookingsFieldsEnabled={isEnabled}
              startingPoint={startingPoint}
            />
          )}
        </CustomModal>
      </>
    );
  }
);

export default AliasesList;
